import type { DeviceStatus, DisplaySettings, OutputState, Telemetry } from '@glyph/protocol-ts';
import type { CaptionMockStyle, Position } from '@glyph/ui';
import { alignToPosition } from '@glyph/ui';

/** Wrap text by character budget into at most `maxLines` lines (newest last), like the design's mock. */
export function wrapLines(text: string, maxChars: number, maxLines: number): string[] {
  const lines: string[] = [];
  for (const w of text.split(/\s+/).filter(Boolean)) {
    const last = lines[lines.length - 1];
    const cand = last ? `${last} ${w}` : w;
    if (last && cand.length > maxChars) lines.push(w);
    else if (lines.length) lines[lines.length - 1] = cand;
    else lines.push(cand);
  }
  return lines.slice(-maxLines);
}

/** The renderer wraps by width, not characters; estimate the chars that fit at 1920 px for the mock. */
export function charsPerLine(d: DisplaySettings): number {
  const usable = (1920 * d.width_pct) / 100 - (d.box.enabled ? 2 * d.box.padding_x : 0);
  const avg = d.font_size_px * 0.52 + d.letter_spacing_px;
  return Math.max(10, Math.floor(usable / Math.max(8, avg)));
}

/** Lines to draw for the CSS mock given the live caption state and the display mode. */
export function captionLines(finals: { text: string }[], partial: string, d: DisplaySettings): string[] {
  const chars = charsPerLine(d);
  if (d.mode === 'popon') {
    const src = d.popon_show_interim && partial ? partial : (finals[finals.length - 1]?.text ?? '');
    return wrapLines(src, chars, d.max_lines);
  }
  const text = [...finals.slice(-3).map((f) => f.text), partial].filter(Boolean).join(' ');
  return wrapLines(text, chars, d.max_lines);
}

export const displayToPosition = (d: DisplaySettings): Position => alignToPosition(d.v_align, d.h_align);
export const pctToPx = (pct: number) => Math.round((pct / 100) * 1920);
export const pxToPct = (px: number) => (px / 1920) * 100;

export function displayToMock(d: DisplaySettings, lines: string[]): CaptionMockStyle {
  return {
    fontFamily: d.font_family,
    fontWeight: d.font_weight,
    fontSizePx: d.font_size_px,
    color: d.text_color,
    uppercase: d.text_transform === 'upper',
    tracking: d.letter_spacing_px * 2,
    outline: d.outline.enabled || d.text_shadow.enabled,
    box: d.box.enabled ? (d.box.per_line ? 'per_line' : 'band') : 'none',
    boxOpacity: d.box.opacity / 100,
    v: d.v_align,
    h: d.h_align,
    marginPx: pctToPx(d.safe_area_pct),
    offsetXPx: pctToPx(d.offset_x_pct),
    offsetYPx: pctToPx(d.offset_y_pct),
    lines,
    lineHeight: d.line_height,
    textAlign: d.text_align,
    italic: d.font_style === 'italic',
    outlineStyle: d.outline.enabled ? { color: d.outline.color, width: d.outline.width } : undefined,
    textShadow: d.text_shadow.enabled
      ? {
          color: d.text_shadow.color,
          blur: d.text_shadow.blur,
          x: d.text_shadow.offset_x,
          y: d.text_shadow.offset_y,
          opacity: d.text_shadow.opacity / 100,
        }
      : undefined,
    boxColor: d.box.color,
    boxPaddingX: d.box.padding_x,
    boxPaddingY: d.box.padding_y,
    boxRadius: d.box.radius,
    boxShadow: d.box_shadow.enabled
      ? {
          color: d.box_shadow.color,
          blur: d.box_shadow.blur,
          x: d.box_shadow.offset_x,
          y: d.box_shadow.offset_y,
          opacity: d.box_shadow.opacity / 100,
        }
      : undefined,
  };
}

export type PreviewBg = 'chroma' | 'black' | 'transparent';
/** The output carries no alpha: the key colour is either a chroma green or black; "transparent" is preview-only. */
export const previewBgOf = (keyColor: string, transparentPreview: boolean): PreviewBg =>
  transparentPreview ? 'transparent' : keyColor.toLowerCase() === '#000000' ? 'black' : 'chroma';
export const bgLabel = (keyColor: string, blank: boolean, _transparentPreview = false) =>
  blank ? 'BLANK · KEY COLOUR' : `KEY ${keyColor.toUpperCase()}`;
export const CHROMA_DEFAULT = '#00B140';
/** Key colour presets: the common chroma greens and blues, plus black and white for non-keyed feeds. */
export const KEY_PRESETS: readonly { hex: string; label: string }[] = [
  { hex: '#00B140', label: 'Chroma green' },
  { hex: '#00FF00', label: 'Pure green' },
  { hex: '#0047BB', label: 'Chroma blue' },
  { hex: '#FF00FF', label: 'Magenta' },
  { hex: '#000000', label: 'Black' },
  { hex: '#FFFFFF', label: 'White' },
];

/** Design "scroll speed ×": 1.0 = the renderer default 250 ms roll. */
export const speedFromMs = (ms: number) => Math.round((250 / Math.max(60, ms)) * 10) / 10;
export const msFromSpeed = (x: number) => Math.round(250 / Math.max(0.5, x));

export const FONT_FAMILIES = [
  'Helvetica Neue',
  'Atkinson Hyperlegible',
  'IBM Plex Sans',
  'Geist',
  'Geist Mono',
  'Inter',
] as const;

/** Layout id per connector (outputs.assignments). `blank` is rendered as a blank overlay on that output. */
export const LAYOUT_OPTIONS = [
  { value: 'main', label: 'Captions · main layout' },
  { value: 'alt', label: 'Captions · alternate layout' },
  { value: 'monitor', label: 'Operator monitor' },
  { value: 'blank', label: 'Blank · boot colour' },
] as const;
export const layoutLabel = (id: string) => LAYOUT_OPTIONS.find((o) => o.value === id)?.label ?? id;

export const MODE_OVERRIDES = [
  { value: '', label: 'Follow EDID (recommended)' },
  { value: '1920x1080@59.94', label: '1920 × 1080p 59.94' },
  { value: '1920x1080@50', label: '1920 × 1080p 50' },
  { value: '1920x1080@29.97', label: '1920 × 1080p 29.97' },
  { value: '1280x720@59.94', label: '1280 × 720p 59.94' },
] as const;

export const UPLINK_LABEL: Record<Telemetry['uplink'], string> = {
  eth: 'Ethernet',
  wifi: 'Wi-Fi',
  lte: 'LTE',
  none: 'No uplink',
};

export const fmtMode = (o?: OutputState | null) =>
  o?.connected ? `${o.width} × ${o.height}p · ${o.refresh_hz} Hz` : 'not connected';
export const outputByName = (outs: OutputState[] | undefined, name: string) =>
  outs?.find((o) => o.connector === name || o.connector.endsWith(name.slice(-1)));

/** unix seconds -> hh:mm:ss (UTC). */
export const fmtTs = (ts: number) => new Date(ts * 1000).toISOString().slice(11, 19);
/** unix seconds -> "16 Oct 2026 · 09:02". */
export const fmtDateTs = (ts: number | null | undefined, withTime = true) => {
  if (!ts) return '—';
  const d = new Date(ts * 1000);
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  return withTime
    ? `${date} · ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })}`
    : date;
};
/** Timecode of the current moment for a recording started at `tc_start`, `fps` frames. */
export const fmtTc = (startTc: string, elapsedS: number, fps = 30) => {
  const m = /^(\d\d):(\d\d):(\d\d)[:;](\d\d)$/.exec(startTc);
  const base = m ? +m[1]! * 3600 + +m[2]! * 60 + +m[3]! : 0;
  const t = base + Math.floor(elapsedS);
  const z = (n: number) => String(n).padStart(2, '0');
  const f = Math.floor(((Date.now() % 1000) / 1000) * fps);
  return `${z(Math.floor(t / 3600) % 24)}:${z(Math.floor((t % 3600) / 60))}:${z(t % 60)}${fps === 29.97 ? ';' : ':'}${z(f)}`;
};
export const upper = (s: string | null | undefined) => (s ?? '').toUpperCase();

export const isBlank = (st?: DeviceStatus) => st?.engine?.overlay === 'blank';
export const deviceStateOf = (st?: DeviceStatus, online = true) =>
  st?.engine?.locked ? 'locked' : st?.engine?.running ? 'live' : online ? 'standby' : 'offline';
